import { useRef, useState } from "react";
import { Upload, Loader2, Film, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { uploadFileChunked } from "@/lib/chunkUpload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function resolveMediaUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) {
    const full = `${BACKEND_URL}${url}`;
    if (url.startsWith("/api/files/")) {
      const token = localStorage.getItem("rl_token");
      if (token) return `${full}${url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`;
    }
    return full;
  }
  return url;
}

// Verkleinerte Server-Thumbnails für Karten-Vorschauen (nur eigene /api/files/-Bilder)
export function thumbUrl(url, w = 480) {
  const u = resolveMediaUrl(url);
  if (!u || !u.includes("/api/files/")) return u;
  return `${u}${u.includes("?") ? "&" : "?"}w=${w}`;
}

// Upload control for video/image. Calls onUploaded({url, kind}) per Datei.
// multiple: erlaubt Mehrfachauswahl; onBatchUploaded(results) liefert alle Ergebnisse gesammelt.
export function FileUpload({
  onUploaded,
  onBatchUploaded,
  onUploadStart,
  onUploadEnd,
  accept = "video/*,image/*",
  label = "Medien hochladen",
  dark = false,
  multiple = false,
  folderTitle = "Allgemein",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [fileNo, setFileNo] = useState([0, 0]);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    onUploadStart?.(files);
    const results = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setFileNo([i + 1, files.length]);
        setPct(0);
        try {
          const data = await uploadFileChunked(files[i], { onProgress: setPct, folderTitle });
          results.push(data);
          await onUploaded?.(data);
        } catch (err) {
          toast.error(err.response?.data?.detail || `Upload von „${files[i].name}“ fehlgeschlagen (${err.response?.status || "Netzwerkfehler"})`);
        }
      }
      if (results.length) {
        try {
          await onBatchUploaded?.(results);
          toast.success(results.length > 1 ? `${results.length} Dateien hochgeladen` : "Upload erfolgreich");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Die Datei wurde hochgeladen, konnte dem Beitrag aber nicht zugeordnet werden.");
        }
      }
    } finally {
      setBusy(false);
      setPct(0);
      setFileNo([0, 0]);
      if (inputRef.current) inputRef.current.value = "";
      onUploadEnd?.(results);
    }
  };

  const base = dark
    ? "bg-neutral-800/80 text-neutral-100 border-neutral-700 hover:bg-neutral-700"
    : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50";

  const busyLabel = fileNo[1] > 1
    ? `Datei ${fileNo[0]}/${fileNo[1]} · ${pct}%`
    : pct > 0 ? `Lädt hoch… ${pct}%` : "Lädt hoch…";

  return (
    <>
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} onChange={handle} className="hidden" data-testid="file-upload-input" />
      <button
        type="button"
        data-testid="file-upload-btn"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className={`inline-flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-lg border transition-colors disabled:opacity-60 ${base}`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {busy ? busyLabel : label}
      </button>
    </>
  );
}

export { Film, ImageIcon };

// Führt Batch-Upload-Ergebnisse mit dem Formular-State zusammen — ab 2 Bildern wird automatisch ein Karussell gebaut
export function mergeUploadsIntoForm(s, items) {
  const videos = items.filter((x) => x.kind === "video");
  const images = items.filter((x) => x.kind !== "video");
  const next = { ...s };
  if (videos.length) next.video_url = videos[videos.length - 1].url;
  if (!images.length) return next;
  const base = (s.carousel_urls || []).length ? s.carousel_urls : (s.media_url ? [s.media_url] : []);
  if (!base.length && images.length === 1) {
    next.media_url = images[0].url;
  } else {
    const merged = [...base, ...images.map((i) => i.url)].filter((u, i, a) => a.indexOf(u) === i).slice(0, 10);
    next.carousel_urls = merged;
    next.media_url = merged[0];
    if (!next.video_url && merged.length >= 2) next.content_type = "carousel";
  }
  return next;
}

// Ersetzt das bisherige Beitragsmedium vollständig. Das ist bewusst getrennt
// vom "Anhängen"-Verhalten in mergeUploadsIntoForm: Im Bearbeitungsdialog
// darf kein altes Video oder Karussell das neu gewählte Medium überlagern.
export function replaceUploadsInForm(s, items) {
  if (!items?.length) return s;
  const videos = items.filter((x) => x.kind === "video");
  const images = items.filter((x) => x.kind !== "video");

  if (videos.length) {
    return {
      ...s,
      video_url: videos[videos.length - 1].url,
      media_url: "",
      carousel_urls: [],
      content_type: s.content_type === "carousel" ? "reel" : s.content_type,
    };
  }

  const urls = images.map((x) => x.url).filter(Boolean).slice(0, 10);
  if (!urls.length) return s;
  if (urls.length === 1) {
    return {
      ...s,
      video_url: "",
      media_url: urls[0],
      carousel_urls: [],
      content_type: s.content_type === "carousel" ? "post" : s.content_type,
    };
  }
  return {
    ...s,
    video_url: "",
    media_url: urls[0],
    carousel_urls: urls,
    content_type: "carousel",
  };
}
